import { groupPosts, groupPostsReply } from '@/utils/groupPosts';

import { getActiveTab } from '../../utils/activeTabUtils.js';

export default {
  // Global UI state
  setRouteLoading(state, isLoading) {
    state.isRouteLoading = !!isLoading;
  },
  setPosts(state, payload) {
    state.recomendedPosts = payload;
  },

  setRecomendedPosts(state, payload) {
    if (!state || !payload) return;
    const groupedPosts = groupPosts(payload);
    state.recomendedPosts = groupedPosts;
  },
  setFollowingPosts(state, { payload, postType }) {
    if (!state || !payload) return;
    // Use same simple grouping as For All feed to ensure consistent rendering
    // Provide id fallback if API returns _id
    const normalizedSource = payload.map(p => {
      if (p && !p.id && p._id) {
        return { ...p, id: p._id };
      }
      return p;
    });
    const grouped = groupPosts(normalizedSource);
    // Normalize minimal authorUser object to satisfy Post component v-if guards
    state.followingPosts = grouped.map(p => {
      if (!p.authorUser && (p.authorUserId || p.authorUserID)) {
        const uid = p.authorUserId || p.authorUserID;
        const uname = p.username || p.handle || p.authorUsername || `user_${uid}`;
        return {
          ...p,
          authorUser: {
            id: uid,
            username: uname,
            rating: p.authorUserRating || 0,
            roles: Array.isArray(p.authorUserRoles) ? p.authorUserRoles : [],
            avatarFile: p.authorAvatar || p.avatarFile || { storageId: null },
          },
        };
      }
      return p;
    });
    // Removed debug logging for production
  },
  // setPostsForyou(state, { collection, posts }) {
  //   if (state[collection]) {
  //     state[collection] = posts;
  //   }
  // },
  setReportBotId(state, id) {
    state.reportId = id;
  },
  setPostsSuggested(state, payload) {
    state.suggestedPosts = payload;
  },
  setPost(state, payload) {
    state.recomendedPost = payload;
  },

  setRepliePost(state, payload) {
    state.replies = payload;
  },

  // Add optimistic reply to local state immediately
  addOptimisticReply(state, reply) {
    // Create a temporary reply object with the expected structure
    const optimisticReply = {
      ...reply,
      id: `temp-${Date.now()}`, // Temporary ID
      isOptimistic: true, // Flag to identify optimistic updates
      createdAt: new Date().toISOString(),
    };
    // Add to the end of the replies array (newest first)
    state.replies = [...state.replies, optimisticReply];
  },

  // Remove optimistic reply when real reply is loaded
  removeOptimisticReply(state, tempId) {
    state.replies = state.replies.filter(reply => reply.id !== tempId);
  },

  async postPost(state, payload) {
    state.post = payload.data;
    const activeTab = getActiveTab();
    if (activeTab && activeTab.collection) {
      if (!state[activeTab.collection]) {
        state[activeTab.collection] = [];
      }
      if (payload.type === 'create') {
        if (activeTab.collection === 'positive') {
          state.positive = Array.isArray(state.positive) ? state.positive : [];
          state.positive.unshift(state.post);
          if (Array.isArray(state.negative) && state.negative.length > 0) {
            state.negative.unshift(state.post);
          }
        }
        if (activeTab.collection === 'negative') {
          state.negative = Array.isArray(state.negative) ? state.negative : [];
          state.negative.unshift(state.post);
          if (Array.isArray(state.positive) && state.positive.length > 0) {
            state.positive.unshift(state.post);
          }
        }
        if (activeTab.collection !== 'negative' && activeTab.collection !== 'positive') {
          if (
            payload.data.type !== 'reply' &&
            activeTab.collection !== 'userReplies' &&
            activeTab.collection !== 'userReposts'
          ) {
            state[activeTab.collection].unshift(state.post);
          }
          if (
            activeTab.collection === 'userReplies' ||
            activeTab.collection === 'userReposts' ||
            !activeTab.collection
          ) {
            state.onlyUserPosts.unshift(state.post);
          }
          if (Array.isArray(state.positive) && state.positive.length > 0) {
            state.positive.unshift(state.post);
          }
          if (Array.isArray(state.negative) && state.negative.length > 0) {
            state.negative.unshift(state.post);
          }
        }
      }
      if (payload.type === 'update') {
        if (activeTab.collection === 'recomendedPosts' || activeTab.collection === 'followingPosts') {
          let index = await state[activeTab.collection].findIndex(el => el.id === state.post.id);
          if (index !== -1) {
            state[activeTab.collection].splice(index, 1, state.post);
          }
        }
        if (activeTab.collection === 'positive') {
          let indexP = await state.positive.findIndex(el => el.id === state.post.id);
          let indexN = await state.negative.findIndex(el => el.id === state.post.id);
          state[activeTab.collection].splice(indexP, 1, state.post);
          if (indexN !== -1) {
            state.negative.splice(indexN, 1, state.post);
          }
        }
        if (activeTab.collection === 'negative') {
          let indexN = await state.negative.findIndex(el => el.id === state.post.id);
          let indexP = await state.positive.findIndex(el => el.id === state.post.id);
          state[activeTab.collection].splice(indexN, 1, state.post);
          if (indexP !== -1) {
            state.positive.splice(indexP, 1, state.post);
          }
        }
        if (state.onlyUserPosts) {
          const index = state.onlyUserPosts.findIndex(post => post.id === state.post.id);
          if (index !== -1) {
            state.onlyUserPosts.splice(index, 1, state.post);
          }
        }
      }
    }
    if (payload.data.type === 'reply') {
      const index = state.replies.findIndex(reply => reply.id === payload.data.id);
      if (index !== -1) {
        state.replies.splice(index, 1, payload.data);
      }
    }
  },

  removePost(state, postId) {
    try {
      const activeTab = getActiveTab();
      if (!activeTab) {
        console.warn('Remove post: Active tab not found');
        return;
      }

      const collection = state[activeTab.collection];
      if (!collection) {
        console.warn('Remove post: Collection not found');
        return;
      }

      if (Array.isArray(collection)) {
        state[activeTab.collection] = collection.filter(item => item?.id !== postId);
      } else {
        delete collection[postId];
      }
    } catch (error) {
      console.error('Error removing post:', error);
    }
  },

  async setCollection(state, { collectionName, data, isReply }) {
    // Normalize inputs defensively
    const incoming = Array.isArray(data) ? data : (data && data.results && Array.isArray(data.results) ? data.results : []);
    if (!Array.isArray(state[collectionName])) {
      // Initialize target collection if missing
      state[collectionName] = [];
    }
    if (Array.isArray(state[collectionName])) {
      // Helper: merge arrays preserving order and ensuring unique ids
      const mergeUniqueById = (existing = [], incoming = []) => {
        const seen = new Set();
        const result = [];
        for (const item of existing) {
          const id = item && item.id;
          if (id == null || !seen.has(id)) {
            result.push(item);
            if (id != null) seen.add(id);
          }
        }
        for (const item of incoming) {
          const id = item && item.id;
          if (id == null || !seen.has(id)) {
            result.push(item);
            if (id != null) seen.add(id);
          }
        }
        return result;
      };

      if (collectionName === 'recomendedPosts' || collectionName === 'followingPosts') {
        // Modern social media feeds display posts individually in chronological order
        let processedPosts = incoming;
        if (isReply?.type === 'Reply') {
          processedPosts = groupPostsReply(incoming);
        }
        state[collectionName] = mergeUniqueById(state[collectionName], processedPosts);
      } else {
        state[collectionName] = mergeUniqueById(state[collectionName], incoming);
      }
    } else {
      state[collectionName] = [...incoming];
    }
  },

  updateNow(state) {
    state.now = new Date();
  },
  setProfile(state, profile) {
    state.profile = profile;
  },
  setUserReplies(state, replies) {
    state.userReplies = replies;
  },
  setUserReposts(state, reposts) {
    state.userReposts = reposts;
  },
  setOnlyUserPosts(state, posts) {
    state.onlyUserPosts = posts;
  },

  setReputationCategories(state, categories) {
    state.reputationCategories = categories;
  },
  setStatusLoad(state, payload) {
    state.loadStatus = payload;
  },
  setIsReply(state, isReply) {
    state.isReply = isReply;
  },
  setUsernameData(state, data) {
    state.usernameData = data;
  },
  removeReportedPost(state, id) {
    if (Array.isArray(state.recomendedPosts)) {
      state.recomendedPosts = state.recomendedPosts.filter(post => post.id !== id);
    }
  },

  removeReportedPostWithCollection(state, { postId, collectionName }) {
    if (!state[collectionName]) {
      console.warn(`Collection "${collectionName}" does not exist.`);
      return;
    }
    state.collections[collectionName] = state.collections[collectionName].filter(post => post.id !== postId);
  },

  setReaction(state, payload) {
    state.replies = state.replies.map(post => (post.id === payload.id ? payload : post));
  },
  updatePostReactions(state, { postId, reactionSets }) {
    const apply = (arr) => {
      if (!Array.isArray(arr)) return;
      const idx = arr.findIndex(p => p && p.id === postId);
      if (idx !== -1) {
        const updated = { ...arr[idx], reactionSets: Array.isArray(reactionSets) ? reactionSets : [] };
        arr.splice(idx, 1, updated);
      }
    };
    // Update current detailed post
    if (state.recomendedPost && state.recomendedPost.id === postId) {
      state.recomendedPost = { ...state.recomendedPost, reactionSets: Array.isArray(reactionSets) ? reactionSets : [] };
    }
    // Update common collections
    apply(state.recomendedPosts);
    apply(state.followingPosts);
    apply(state.onlyUserPosts);
    apply(state.replies);
    apply(state.positive);
    apply(state.negative);
  },
  setIntegratedRepliesForPost(state, { postId, replies, total }) {
    const apply = (arr) => {
      if (!Array.isArray(arr)) return;
      const idx = arr.findIndex(p => p && p.id === postId);
      if (idx !== -1) {
        const post = arr[idx];
        const updated = {
          ...post,
          hasIntegratedReplies: true,
          integratedReplies: replies,
          totalRepliesCount: typeof total === 'number' ? total : (replies?.length || 0),
        };
        arr.splice(idx, 1, updated);
      }
    };
    apply(state.recomendedPosts);
    apply(state.followingPosts);
    if (state.recomendedPost && state.recomendedPost.id === postId) {
      state.recomendedPost = {
        ...state.recomendedPost,
        hasIntegratedReplies: true,
        integratedReplies: replies,
        totalRepliesCount: typeof total === 'number' ? total : (replies?.length || 0),
      };
    }
  },
  addReplyPostStatus(state, status) {
    state.replyPostStatus = status;
  },
  setTotalCountPosts(state, count) {
    state.totalCountPosts = count;
  },
  // setBlockOnReplies(state, blockedUsersIds) {
  //   state.replies.splice(
  //     0,
  //     state.replies.length,
  //     ...state.replies.filter((reply) => !blockedUsersIds.includes(reply.authorUserId))
  //   );
  // },
  setSearchText(state, text) {
    state.searchText = text;
  },
  setSearchResults(state, { users, posts, groups }) {
    state.searchResults.users = users;
    state.searchResults.posts = posts;
    state.searchResults.groups = groups || [];
  },
  setNoSearchResults(state, query) {
    state.noSearchResults = query;
  },
  clearSearchResults(state) {
    state.searchResults = { users: [], posts: [], groups: [] };
  },
  clearCollection(state, collectionName) {
    if (state[collectionName]) {
      state[collectionName] = [];
    }
  },
  setUnreadPosts(state, posts) {
    state.unreadPosts = posts;
  },
  setIntroPosts(state, posts) {
    state.introPosts = posts;
  },
};
